"use client"

import Link from "next/link"
import Image from "next/image"
import { ArrowRight, BookOpen, Search, Tags, X } from "lucide-react"
import { BlogCategory, BlogPost } from "@lib/data/blog"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

type BlogIndexTemplateProps = {
  posts: BlogPost[]
  categories: BlogCategory[]
}

export default function BlogIndexTemplate({
  posts,
  categories,
}: BlogIndexTemplateProps) {
  const { i18n, t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const locale = i18n.language === "tr" ? "tr-TR" : "en-US"

  const allTags = useMemo(() => {
    return Array.from(new Set(posts.flatMap((post) => post.tags))).sort(
      (a, b) => a.localeCompare(b, locale)
    )
  }, [locale, posts])

  const filteredPosts = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)

    return posts.filter((post) => {
      const searchableText = [
        post.title,
        post.excerpt,
        post.category.title,
        post.tags.join(" "),
      ]
        .join(" ")
        .toLocaleLowerCase(locale)

      const matchesSearch = query ? searchableText.includes(query) : true
      const matchesTags =
        selectedTags.length > 0
          ? selectedTags.some((tag) => post.tags.includes(tag))
          : true

      return matchesSearch && matchesTags
    })
  }, [locale, posts, searchQuery, selectedTags])

  const featuredPost = filteredPosts[0]
  const secondaryPosts = filteredPosts.slice(1)

  const toggleTag = (tag: string) => {
    setSelectedTags((currentTags) =>
      currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag]
    )
  }

  const clearFilters = () => {
    setSearchQuery("")
    setSelectedTags([])
  }

  return (
    <main className="bg-slate-50">
      <section className="relative overflow-hidden border-b border-orange-100 bg-white">
        <Image
          src="/images_public/101yapiBanner.png"
          alt=""
          fill
          priority
          className="scale-105 object-cover blur-sm"
          sizes="100vw"
          unoptimized
        />
        <div className="absolute inset-0 bg-white/78" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/88 to-white/55" />
        <div className="content-container relative grid gap-8 px-4 py-10 small:px-6 lg:grid-cols-[minmax(0,760px),1fr] lg:py-14">
          <div className="flex flex-col justify-center gap-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">
              {t("blog.brand")}
            </p>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 lg:text-5xl">
              {t("blog.heroTitle")}
            </h1>
            <p className="max-w-2xl text-base leading-7 text-slate-600">
              {t("blog.heroDescription")}
            </p>
          </div>
        </div>
      </section>

      <section className="content-container px-4 pt-8 small:px-6">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),auto] lg:items-start">
            <div className="space-y-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t("blog.searchPlaceholder")}
                  className="h-11 w-full rounded-md border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-orange-300 focus:ring-2 focus:ring-orange-100"
                />
              </div>
              {allTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag)
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                          isSelected
                            ? "border-orange-500 bg-orange-100 text-orange-800"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:border-orange-200 hover:text-orange-700"
                        }`}
                      >
                        {tag}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            {(searchQuery || selectedTags.length > 0) && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-semibold text-slate-600 transition hover:border-orange-200 hover:text-orange-700"
              >
                <X className="h-4 w-4" />
                {t("blog.clearFilters")}
              </button>
            )}
          </div>
          <p className="mt-4 text-sm text-slate-500">
            {t("blog.postsShowing", { count: filteredPosts.length })}
          </p>
        </div>
      </section>

      <section className="content-container px-4 py-8 small:px-6 lg:py-10">
        {featuredPost ? (
          <Link
            href={`/blog/${featuredPost.slug}`}
            className="grid overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:border-orange-200 hover:shadow-md lg:grid-cols-[1.1fr,0.9fr]"
          >
            <div className="relative min-h-[280px]">
              <Image
                src={featuredPost.coverImage}
                alt={featuredPost.coverAlt}
                fill
                className="object-cover"
                priority
                unoptimized
              />
            </div>
            <div className="flex flex-col justify-center gap-4 p-6 lg:p-8">
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="rounded-full bg-orange-100 px-3 py-1 font-semibold text-orange-700">
                  {t("blog.featured")}
                </span>
                <span>{featuredPost.category.title}</span>
                <span>
                  {t("blog.readTime", { count: featuredPost.readTimeMinutes })}
                </span>
              </div>
              <h2 className="text-3xl font-black tracking-tight text-slate-950">
                {featuredPost.title}
              </h2>
              <p className="text-base leading-7 text-slate-600">
                {featuredPost.excerpt}
              </p>
              <span className="inline-flex items-center gap-2 font-semibold text-orange-700">
                {t("blog.readArticle")} <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-slate-600">
            {t("blog.noPosts")}
          </div>
        )}
      </section>

      <section className="content-container grid gap-6 px-4 pb-10 small:px-6 lg:grid-cols-[1fr,320px]">
        <div className="grid gap-5 sm:grid-cols-2">
          {secondaryPosts.map((post) => (
            <article
              key={post.slug}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
            >
              <Link href={`/blog/${post.slug}`} className="block">
                <div className="relative aspect-[16/9]">
                  <Image
                    src={post.coverImage}
                    alt={post.coverAlt}
                    fill
                    className="object-cover"
                    unoptimized
                  />
                </div>
                <div className="space-y-3 p-5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-orange-700">
                    <BookOpen className="h-4 w-4" />
                    {post.category.title}
                  </div>
                  <h2 className="text-xl font-bold text-slate-950">
                    {post.title}
                  </h2>
                  <p className="line-clamp-3 text-sm leading-6 text-slate-600">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>
                      {t("blog.readTime", { count: post.readTimeMinutes })}
                    </span>
                    <span className="font-semibold text-orange-700">
                      {t("blog.read")}
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-950">
              <Tags className="h-5 w-5 text-orange-600" />
              {t("blog.categories")}
            </h2>
            <div className="space-y-3">
              {categories.map((category) => (
                <div key={category.slug} id={category.slug}>
                  <p className="font-semibold text-slate-900">
                    {category.title}
                  </p>
                  <p className="text-sm leading-6 text-slate-500">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          {allTags.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-950">
                <Tags className="h-5 w-5 text-orange-600" />
                {t("blog.tags")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      selectedTags.includes(tag)
                        ? "border-orange-500 bg-orange-100 text-orange-800"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-orange-200 hover:text-orange-700"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </section>
    </main>
  )
}
